import React from 'react';
import moment from 'moment';
import { Common } from '../../utils/common';
import _ from 'lodash';
import CallProcessor from '../../call_rescoring/CallProcessor';
import { Permissions } from '../../utils/permissions';

/**
 * @param {{
 *   tableData: Array;
 *   isAutomotiveIndustry: boolean;
 * }} props
 */
export default function CallDetailsTable({ tableData, isAutomotiveIndustry }) {
  const tableEl = React.useRef(null);
  const [callId, setCallId] = React.useState(null);

  const columns = React.useMemo(() => {
    const tagGroupColumns = [];
    if (tableData.length > 0) {
      const tagGroupCount = tableData[0].tag_group_count;
      for (let i = 1; i <= tagGroupCount; i++) {
        const title = tableData[0][`r${i}_tag_group_name`];
        const data = `r${i}_tag_name`;

        tagGroupColumns.push({
          data,
          title,
          visible: false,
        });
      }
    }

    const columns = [];
    columns.push({
      data: null,
      render: function () {
        return '&nbsp;';
      },
      searchable: false,
      orderable: false,
      className: 'noExport',
    });
    columns.push({
      data: 'callsourceid',
      title: 'Static_Call_ID',
      visible: false,
    });
    columns.push({
      data: 'aicallid',
      title: 'DNI_Call_ID',
      visible: false,
    });
    columns.push({
      data: null,
      title: 'Audio',
      width: '175px',
      render: (_data, _type, row) => {
        let audio_url;
        if (row.audiourl) {
          audio_url = row.audiourl;
        } else {
          audio_url = row.callsource_audio_url;
        }

        if (!audio_url) {
          return '';
        }

        let html = `<div style="display: flex; align-items: center; position: relative;">`;

        // AT-2642
        // 1. if the call is not redacted, show the audio
        // 2. if the call needs to be redacted, only show the audio when the redaction is completed.
        if (
          !row.transcription_needs_redaction ||
          (row.transcription_needs_redaction && row.transcription_completed)
        ) {
          html += `<audio-player src="${audio_url}"></audio-player>`;
        }

        html += renderTranscriptionViewIcon(audio_url, row);
        html += `</div>`;
        return html;
      },
      searchable: false,
      className: 'noExport',
    });
    columns.push({
      data: 'callsource_audio_url',
      title: 'Audio URL',
      visible: false,
      render(data, type, row) {
        if (
          data &&
          // AT-2642
          // 1. if the call is not redacted, show the audio
          // 2. if the call needs to be redacted, only show the audio when the redaction is completed.
          (!row.transcription_needs_redaction ||
            (row.transcription_needs_redaction && row.transcription_completed))
        ) {
          return `<a href="${data}" target="_blank">${data}</a>`;
        } else {
          return null;
        }
      },
    });
    columns.push({
      data: null,
      title: 'Repeat Caller',
      render: function (data, type, row) {
        if (row.repeat_caller == '1') {
          return 'Repeat Caller <span class="fa fa-check"></span>';
        } else {
          return 'New Caller <span class="fa fa-thumbs-o-up"></span>';
        }
      },

      visible: false,
    });
    columns.push({
      data: null,
      title: 'Time of Call',
      render: function (data, type, row) {
        //return moment.utc(row.call_start_time).local().format('MMM D, YYYY hh:mm:ss A');
        return moment(row.call_start_time).utcOffset(row.call_start_time).format('MMM D, YYYY hh:mm:ss A');
        
      },
    });
    columns.push({
      data: 'landing_domain',
      title: 'Domain',
      visible: false,
    });

    columns.push({
      data: null,
      title: 'Caller ID',
      render: (data, type, row) => {
        let callerID;
        if (row.ani?.length === 10) {
          callerID = Common.formatPhoneNumber(row.ani);
        } else {
          callerID = row.ani;
        }
        if (
          callerID &&
          isAutomotiveIndustry &&
          row.global_id &&
          row.global_id !== 'unknown' &&
          !Common.in_crm_mode() &&
          !window.IN_SCHEDULED_REPORT_MODE
        ) {
          return `<a href="/visitors/${row.global_id}/activity/${row.site}" target="_self">${callerID}</a>`;
        } else {
          return callerID ? callerID : null;
        }
      },
    });

    columns.push({
      data: null,
      title: 'Caller Name',
      render: (data, type, row) => {
        if (row.dscallername) {
          return row.dscallername
            .split(' ')
            .map((word) => _.capitalize(word))
            .join(' ');
        } else {
          return `${_.capitalize(row.first_name)} ${_.capitalize(row.last_name)}`;
        }
      },
    });

    columns.push({
      data: null,
      title: 'Address',
      render: function (data, type, row) {
        var street = row.street_name + ' ' + row.city + ' ' + row.state + ' ' + row.zip;
        return street;
      },
      visible: false,
    });

    if (isAutomotiveIndustry) {
      columns.push({
        data: 'vin',
        title: 'VIN',
        visible: false,
      });
      columns.push({
        data: 'year',
        title: 'Year',
        visible: false,
      });
      columns.push({
        data: 'make',
        title: 'Make',
        visible: false,
      });
      columns.push({
        data: 'model',
        title: 'Model',
        visible: false,
      });
      columns.push({
        data: null,
        title: 'Vehicle',
        render: function (data, type, row) {
          if (typeof row.image_url !== 'undefined' && row.image_url !== null) {
            var p1 = "<a href='" + row.landing_url + " target='_blank' class='landing_url'>";
            p1 +=
              "<img src='" +
              row.image_url +
              "' class='vehicle_image' onerror='javascript:Common.imageError(this);' />";
            p1 += '</a>';
            return p1;
          } else {
            return null;
          }
        },
        searchable: false,
        className: 'vehicle_image_container table_cell_centered_text noExport',
        visible: false,
      });
    }

    columns.push({
      data: null,
      title: 'Tracking',
      render: function (data, type, row) {
        return Common.formatPhoneNumber(row.tracking);
      },
    });
    columns.push({
      data: null,
      title: 'Target',
      render: function (data, type, row) {
        return Common.formatPhoneNumber(row.target) || row.target;
      },
      visible: false,
    });
    columns.push({
      data: 'adsource',
      title: 'ADSource',
      visible: false,
    });

    columns.push(...tagGroupColumns);

    columns.push({
      data: 'ref_domain',
      title: 'Referral',
      visible: false,
    });
    columns.push({
      data: 'ref_medium',
      title: 'Medium',
      visible: false,
    });
    columns.push({
      data: 'utm_source',
      title: 'UTM Source',
      visible: false,
    });
    columns.push({
      data: 'utm_medium',
      title: 'UTM Medium',

      visible: false,
    });
    columns.push({
      data: 'utm_campaign',
      title: 'UTM Campaign',
      visible: false,
    });
    columns.push({
      data: 'utm_content',
      title: 'UTM Content',
      visible: false,
    });
    columns.push({
      data: 'utm_term',
      title: 'Keyword',
      visible: false,
    });
    columns.push({
      data: 'gclid',
      title: 'GCLID',
      visible: false,
    });
    columns.push({
      data: 'landing_url',
      title: 'RAW URL Called On',
      visible: false,
    });
    columns.push({
      data: 'employeename',
      title: 'Representative Name',
      className: 'whitespace_nowrap',
      render: (data, type, row) => {
        if (window.IN_SCHEDULED_REPORT_MODE) {
          return data;
        } else if (type === 'display') {
          if (row.is_autoid == 1) {
            return `
            <representative-code
              employee_name="${data}" 
              aicallid="${row.aicallid}"
              static_call_id="${row.callsourceid}"
              domain_id="${row.domain_id}"
            ></representative-code>
          `;
          } else {
            return data;
          }
        } else {
          return data;
        }
      },
    });
    columns.push({
      data: 'calltypetext',
      title: 'Call Type',
    });
    columns.push({
      data: 'callstatus',
      title: 'Call Status',
      visible: false,
    });
    columns.push({
      data: 'alertreason',
      title: 'Alert Reason',
      visible: false,
    });
    columns.push({
      data: 'altphone',
      title: 'Alternate Phone',
      visible: false,
    });
    columns.push({
      data: 'appointmentset',
      title: 'Appointment Set',
      visible: false,
    });
    columns.push({
      data: 'appointmenttime',
      title: 'Appointment Time',
      visible: false,
    });
    columns.push({
      data: 'department',
      title: 'Department',
      visible: false,
    });
    columns.push({
      data: 'dsinitialcontact',
      title: 'Initial Contact',
      visible: false,
    });
    columns.push({
      data: 'callsummary',
      title: 'Call Summary',
      visible: false,
    });
    columns.push({
      data: null,
      title: 'Duration',
      render: function (data, type, row) {
        return moment
          .utc(moment.duration(parseInt(row.duration), 'seconds').asMilliseconds())
          .format('HH:mm:ss');
      },
    });

    columns.push({
      data: 'ring_count',
      title: 'Ring Count',
      visible: false,
    });
    columns.push({
      data: 'location_name',
      title: 'Location Name',
      visible: false,
    });
    columns.push({
      data: 'location_address',
      title: 'Location Address',
      visible: false,
    });
    columns.push({
      data: 'location_city',
      title: 'Location City',
      visible: false,
    });
    columns.push({
      data: 'location_state',
      title: 'Location State',
      visible: false,
    });
    columns.push({
      data: 'location_zip',
      title: 'Location ZIP',
      visible: false,
    });
    columns.push({
      data: 'campaign',
      title: 'Campaign Name',
    });
    columns.push({
      data: 'dialstatus',
      title: 'Result',
      render: function (data, type, row) {
        return _.capitalize(data == 'ANSWER' ? 'CONNECTED' : data);
      },
    });
    if (
      !window.IN_SCHEDULED_REPORT_MODE &&
      Permissions.hasPermission(Permissions.ALL.call_processor_rescore)
    ) {
      columns.push({
        data: null,
        title: 'Rescore',
        render(data, type, row) {
          const callId = row.aicallid || row.callsourceid;
          return `
          <button
            type="button"
            class="btn btn-default btn-xs open-rescore-modal"
            data-toggle="modal"
            data-target="#rescoreModal"
            data-callid="${callId}"
          >
            Rescore
          </button>
        `;
        },
        className: 'noExport',
      });
    }
    return columns;
  }, [isAutomotiveIndustry, tableData]);

  React.useEffect(() => {
    const $el = $(tableEl.current);
    const table = $el.DataTable({
      columns,
      autoWidth: false,
      data: tableData,
      deferRender: true,
      dom: '<fB>tlip',
      lengthMenu: [
        [10, 25, 50, 100],
        [10, 25, 50, 100],
      ],
      drawCallback: function () {
        Common.formatTableValues('table.table');
      },
      fnRowCallback: function (nRow, aData, iDisplayIndex, iDisplayIndexFull) {
        var index = iDisplayIndexFull + 1 + '.';
        $('td:eq(0)', nRow).html(index);
        return nRow;
      },
      stateSave: true,
      stateSaveCallback: function (_oSettings, oData) {
        if (!Common.in_crm_mode()) {
          localStorage.setItem('DataTables-AI-Call_Details', JSON.stringify(oData));
        }
      },
      stateLoadCallback: function () {
        if (!Common.in_crm_mode()) {
          return JSON.parse(localStorage.getItem('DataTables-AI-Call_Details'));
        }
      },
      buttons: [
        {
          extend: 'colvis',
          text: 'Select Columns',
          titleAttr: 'Select Columns',
          collectionLayout: 'three-column',
          columns: ':gt(0)',
        },
        {
          extend: 'excelHtml5',
          text: 'Export Selected',
          title: 'Call_Details_Export',
          titleAttr: 'Export as Excel',
          exportOptions: {
            columns(i, data, th) {
              const isVisible = table.column(i).visible();
              const isNotForExport = $(th).hasClass('noExport');
              return isVisible && !isNotForExport ? true : false;
            },
            orthogonal: 'export',
          },
        },
        {
          extend: 'excelHtml5',
          text: 'Export All',
          title: 'Call_Details_Export',
          titleAttr: 'Export as Excel',
          exportOptions: {
            columns(i, data, th) {
              if ($(th).hasClass('noExport')) {
                return false;
              } else {
                return true;
              }
            },
            orthogonal: 'export',
          },
        },
      ],
      order: [5, 'asc'],
      columnDefs: [{ className: 'table_cell_centered_text', targets: '_all' }],
      paging: true,
      responsive: true,
      destroy: true,
      rowId(data) {
        return data.aicallid || data.callsourceid;
      },
    });
    return () => {
      // Destroys DataTable instance whenever component unmounts
      table.destroy();
    };
  }, [tableData, isAutomotiveIndustry, columns]);

  // Register event handlers for rescore modal
  React.useEffect(() => {
    $(tableEl.current).on('click', 'button[data-callid]', function () {
      const callId = $(this).data('callid');
      setCallId(callId);
    });

    $('#rescoreModal').on('hidden.bs.modal', function () {
      setCallId(null);
    });
  }, []);

  return (
    <>
      <div className='table-responsive' style={{ paddingTop: '20px' }}>
        <table
          ref={tableEl}
          className='table table-hover table-striped vertical_centered table-bordered'
          style={{ width: '100%' }}
        ></table>
      </div>

      <div
        className='modal fade'
        id='rescoreModal'
        tabIndex={-1}
        role='dialog'
        aria-labelledby='rescoreModalLabel'
      >
        <div className='modal-dialog modal-lg' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <button type='button' className='close' data-dismiss='modal' aria-label='Close'>
                <span aria-hidden='true'>&times;</span>
              </button>
              <h4 className='modal-title' id='rescoreModalLabel'>
                Rescore Call
              </h4>
            </div>
            <div className='modal-body' id='rescore-modal-body'>
              {callId ? (
                <CallProcessor
                  callId={callId}
                  onSaveSuccess={() => {
                    $('#rescoreModal').modal('hide');
                  }}
                />
              ) : (
                <div>Loading...</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/*
  Determines if the transcription view icon should be rendered.
  Logic:
    If a call has speech transcription enabled, show the new transcript view icon 
    only when the transcription job is completed.
*/
function renderTranscriptionViewIcon(audio_url, row) {
  let html = '';

  if (window.IN_SCHEDULED_REPORT_MODE) {
    return html;
  }

  if (row.speech_transcription && row.transcription_completed) {
    const callid = row.aicallid || row.callsourceid;
    html = `
        <transcription-search-viewer-icon
          callid="${callid}"
          domain_id="${row.domain_id}"
          src="${audio_url}"
        >
        </transcription-search-viewer-icon>
      `;
  }

  return html;
}
